import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  FormControlLabel, Switch, Autocomplete, CircularProgress, Box, InputAdornment, IconButton
} from '@mui/material';
import { Close, Visibility, VisibilityOff } from '@mui/icons-material';
//import '../Item/ItemModal.css';

const sectionStyle = { fontSize: '0.7rem', fontWeight: 600, color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5, mt: 1, pb: 0.25, borderBottom: '1px solid #e5e7eb' };
const fieldStyle = { 
  '& .MuiOutlinedInput-root': { '& input, & .MuiSelect-select': { padding: '5px 8px', fontSize: '0.75rem' } }, 
  '& .MuiInputLabel-root': { fontSize: '0.7rem' }, 
  '& .MuiAutocomplete-input': { padding: '5px 8px !important', fontSize: '0.75rem' } 
};
const dropdownStyle = { 
  ...fieldStyle,
  '& .MuiAutocomplete-listbox': { fontSize: '0.75rem' },
  '& .MuiAutocomplete-option': { fontSize: '0.75rem', padding: '4px 8px' }
};
const listboxProps = { sx: { fontSize: '0.75rem', '& .MuiAutocomplete-option': { fontSize: '0.75rem', padding: '4px 8px', minHeight: '28px' } } };

const StaffModal = ({ open, onClose, modalType, modalAction, formData, setFormData, onSave, departments, designations, loading }) => {
  const { companyId, token } = useAuth();
  const [errors, setErrors] = useState({});
  const [balanceSheetHeads, setBalanceSheetHeads] = useState([]);
  const [balanceSheetLoading, setBalanceSheetLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [branchOpen, setBranchOpen] = useState(false);
  const [deptOpen, setDeptOpen] = useState(false);
  const [desigOpen, setDesigOpen] = useState(false);
  const [bsHeadOpen, setBsHeadOpen] = useState(false);

  useEffect(() => {
    if (open && companyId && token && modalType === 'staff') {
      fetchBalanceSheetHeads();
      fetchBranches();
    }
  }, [open, companyId, token, modalType]);

  useEffect(() => {
    if (modalAction === 'create' && open && modalType !== 'staff') {
      setFormData({ Name: '', IsActive: true });
    }
  }, [modalAction, modalType, open, setFormData]);

  const fetchBalanceSheetHeads = async () => {
    setBalanceSheetLoading(true);
    try {
      const response = await fetchData('StaffApi', `/BalanceSheetList/${companyId}`, token);
      setBalanceSheetHeads(response.response_data || response || []);
    } catch (error) { setBalanceSheetHeads([]); }
    setBalanceSheetLoading(false);
  };

  const fetchBranches = async () => {
    try {
      const response = await fetchData('BranchApi', `/${companyId}`, token);
      setBranches(response.response_data || response || []);
    } catch (error) { setBranches([]); }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      // Auto-generate username from FirstName_LastName
      if (field === 'FirstName' || field === 'LastName') {
        const firstName = (field === 'FirstName' ? value : prev.FirstName || '').toLowerCase().replace(/\s+/g, '');
        const lastName = (field === 'LastName' ? value : prev.LastName || '').toLowerCase().replace(/\s+/g, '');
        updated.UserName = lastName ? `${firstName}_${lastName}` : firstName;
      }
      // Close designation dropdown when login toggle is clicked
      if (field === 'IsLogin') {
        setDesigOpen(false);
      }
      return updated;
    });
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (modalType === 'staff') {
      if (!formData.FirstName?.trim()) newErrors.FirstName = 'First name is required';
      if (!formData.UserName?.trim()) newErrors.UserName = 'Username is required';
      if (!formData.Email?.trim()) newErrors.Email = 'Email is required';
      if (!formData.ContactNumber?.trim()) newErrors.ContactNumber = 'Contact number is required';
      if (!formData.BranchId) newErrors.BranchId = 'Branch is required';
      if (!formData.DepartmentId) newErrors.DepartmentId = 'Department is required';
      if (!formData.DesignationId) newErrors.DesignationId = 'Designation is required';
      if (formData.IsLogin && !formData.Password?.trim()) newErrors.Password = 'Password is required';
    } else {
      if (!formData.Name?.trim()) newErrors.Name = 'Name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => { if (validateForm()) onSave(); };

  const getModalTitle = () => {
    const typeNames = { staff: 'Staff', department: 'Department', designation: 'Designation' };
    const actionNames = { create: 'New', edit: 'Edit', view: 'View' };
    return { title: `${actionNames[modalAction] || 'New'} ${typeNames[modalType] || 'Item'}`, subtitle: `${modalAction === 'create' ? 'Add a new' : modalAction === 'edit' ? 'Update' : 'View'} ${(typeNames[modalType] || 'item').toLowerCase()} details` };
  };

  const isDisabled = modalAction === 'view';

  const renderSimpleForm = () => (
    <>
      <Box sx={{ ...sectionStyle, mt: 0 }}>Basic Information</Box>
      <TextField size="small" fullWidth label="Name *" value={formData.Name || ''} onChange={(e) => handleInputChange('Name', e.target.value)} error={!!errors.Name} helperText={errors.Name} disabled={isDisabled} sx={{ mb: 0.75, ...fieldStyle }} />
      <FormControlLabel control={<Switch size="small" checked={formData.IsActive ?? true} onChange={(e) => handleInputChange('IsActive', e.target.checked)} disabled={isDisabled} />} label="Active" sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }} />
    </>
  );

  const renderStaffForm = () => (
    <>
      {/* Basic Information */}
      <Box sx={{ ...sectionStyle, mt: 0 }}>Basic Information</Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0.75, mb: 1.5 }}>
        <TextField size="small" label="First Name *" value={formData.FirstName || ''} onChange={(e) => handleInputChange('FirstName', e.target.value)} error={!!errors.FirstName} helperText={errors.FirstName} disabled={isDisabled} sx={fieldStyle} />
        <TextField size="small" label="Last Name" value={formData.LastName || ''} onChange={(e) => handleInputChange('LastName', e.target.value)} disabled={isDisabled} sx={fieldStyle} />
        <TextField size="small" label="Username *" value={formData.UserName || ''} onChange={(e) => handleInputChange('UserName', e.target.value)} error={!!errors.UserName} helperText={errors.UserName} disabled={isDisabled} sx={fieldStyle} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, mb: 1.5 }}>
        <TextField size="small" label="Email *" type="email" value={formData.Email || ''} onChange={(e) => handleInputChange('Email', e.target.value)} error={!!errors.Email} helperText={errors.Email} disabled={isDisabled} sx={fieldStyle} />
        <TextField size="small" label="Contact Number *" value={formData.ContactNumber || ''} onChange={(e) => handleInputChange('ContactNumber', e.target.value)} error={!!errors.ContactNumber} helperText={errors.ContactNumber} disabled={isDisabled} sx={fieldStyle} inputProps={{ maxLength: 15 }} />
      </Box>

      {/* Organization */}
      <Box sx={sectionStyle}>Organization</Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0.75, mb: 1.5 }}>
        <Autocomplete size="small" open={branchOpen} onOpen={() => setBranchOpen(true)} onClose={() => setBranchOpen(false)} value={branches.find(b => b.Id === formData.BranchId) || null} onChange={(_, v) => handleInputChange('BranchId', v?.Id || null)} options={branches} getOptionLabel={(opt) => opt.Name || ''} disabled={isDisabled} sx={fieldStyle} ListboxProps={listboxProps} disableAutoFocus renderInput={(params) => <TextField {...params} label="Branch *" error={!!errors.BranchId} helperText={errors.BranchId} />} />
        <Autocomplete size="small" open={deptOpen} onOpen={() => setDeptOpen(true)} onClose={() => setDeptOpen(false)} value={departments.find(d => d.Id === formData.DepartmentId) || null} onChange={(_, v) => handleInputChange('DepartmentId', v?.Id || null)} options={departments} getOptionLabel={(opt) => opt.Name || ''} disabled={isDisabled} sx={fieldStyle} ListboxProps={listboxProps} disableAutoFocus renderInput={(params) => <TextField {...params} label="Department *" error={!!errors.DepartmentId} helperText={errors.DepartmentId} />} />
        <Autocomplete 
          size="small" 
          open={desigOpen} 
          onOpen={(event) => {
            // Only open if it's a direct user interaction with the autocomplete
            if (event && event.type === 'click' && event.target && 
                (event.target.closest('.MuiAutocomplete-root') || event.target.closest('.MuiTextField-root'))) {
              setDesigOpen(true);
            }
          }} 
          onClose={() => setDesigOpen(false)} 
          value={designations.find(d => d.Id === formData.DesignationId) || null} 
          onChange={(_, v) => handleInputChange('DesignationId', v?.Id || null)} 
          options={designations} 
          getOptionLabel={(opt) => opt.Name || ''} 
          disabled={isDisabled} 
          sx={fieldStyle} 
          ListboxProps={listboxProps} 
          disableAutoFocus 
          renderInput={(params) => <TextField {...params} label="Designation *" error={!!errors.DesignationId} helperText={errors.DesignationId} />} 
        />
      </Box>

      {/* Login Settings */}
      <Box sx={sectionStyle}>Login Settings</Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr auto auto' }, gap: 0.75, mb: 1.5, alignItems: 'center' }}>
        {formData.IsLogin ? (
          <TextField 
            size="small" 
            label="Password *" 
            type={showPassword ? 'text' : 'password'} 
            value={formData.Password ?? ''} 
            onChange={(e) => handleInputChange('Password', e.target.value)} 
            error={!!errors.Password}
            helperText={errors.Password}
            disabled={isDisabled} 
            sx={fieldStyle}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPassword(!showPassword)} edge="end" disabled={isDisabled}>
                    {showPassword ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        ) : (
          <Box /> // Empty placeholder to maintain grid structure
        )}
        <FormControlLabel 
          control={<Switch size="small" checked={formData.IsLogin ?? false} onChange={(e) => { 
            e.stopPropagation(); // Prevent event bubbling
            document.activeElement?.blur(); 
            setDesigOpen(false); // Ensure designation dropdown is closed
            handleInputChange('IsLogin', e.target.checked); 
          }} disabled={isDisabled} />} 
          label="Allow Login" 
          sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.75rem' }, m: 0 }}
          onClick={(e) => e.stopPropagation()} // Prevent click propagation
        />
        <FormControlLabel control={<Switch size="small" checked={formData.IsActive ?? true} onChange={(e) => { 
          e.stopPropagation(); // Prevent event bubbling
          document.activeElement?.blur(); 
        }} disabled={isDisabled} />} label="Active" sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.75rem' }, m: 0 }} />
      </Box>

      {/* Account Settings */}
      <Box sx={sectionStyle}>Account Settings</Box>
      <Autocomplete size="small" open={bsHeadOpen} onOpen={() => setBsHeadOpen(true)} onClose={() => setBsHeadOpen(false)} value={balanceSheetHeads.find(b => b.Id === formData.BalanceSheetHeadId) || null} onChange={(_, v) => handleInputChange('BalanceSheetHeadId', v?.Id || null)} options={balanceSheetHeads} getOptionLabel={(opt) => opt.Name || ''} loading={balanceSheetLoading} disabled={isDisabled} sx={{ mb: 1.5, ...fieldStyle }} ListboxProps={listboxProps} disableAutoFocus renderInput={(params) => <TextField {...params} label="Balance Sheet Head" InputProps={{ ...params.InputProps, endAdornment: <>{balanceSheetLoading && <CircularProgress size={14} />}{params.InputProps.endAdornment}</> }} />} />

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, mb: 1.5 }}>
        <TextField size="small" label="Opening Balance (₹)" type="number" value={formData.OpeningBalance ?? 0} onChange={(e) => handleInputChange('OpeningBalance', parseFloat(e.target.value) || 0)} disabled={isDisabled} sx={fieldStyle} />
        <TextField size="small" label="Opening Date" type="date" value={formData.OpeningDate?.split('T')[0] || ''} onChange={(e) => handleInputChange('OpeningDate', e.target.value)} disabled={isDisabled} InputLabelProps={{ shrink: true }} sx={fieldStyle} />
      </Box>

      <FormControlLabel control={<Switch size="small" checked={formData.IsPayableOrReceivable ?? false} onChange={(e) => handleInputChange('IsPayableOrReceivable', e.target.checked)} disabled={isDisabled} />} label="Payable/Receivable" sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }} />
    </>
  );

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }} 
      maxWidth={modalType === 'staff' ? 'sm' : 'xs'} 
      fullWidth 
      disableEscapeKeyDown
      className="item-modal"
    >
      <DialogTitle>
        <div className="modal-title-container">
          <div className="modal-title-text"><h2>{getModalTitle().title}</h2><p>{getModalTitle().subtitle}</p></div>
          <Button onClick={onClose} className="modal-close-btn"><Close /></Button>
        </div>
      </DialogTitle>
      <DialogContent sx={{ p: 1.5, pt: 1 }}>{modalType === 'staff' ? renderStaffForm() : renderSimpleForm()}</DialogContent>
      <DialogActions sx={{ px: 1.5, py: 1 }}>
        <Button onClick={onClose} variant="outlined" size="small">Cancel</Button>
        {!isDisabled && <Button onClick={handleSave} variant="contained" size="small" disabled={loading}>{modalAction === 'create' ? `Add ${modalType.charAt(0).toUpperCase() + modalType.slice(1)}` : 'Update'}</Button>}
      </DialogActions>
    </Dialog>
  );
};

export default StaffModal;
